import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useGetQuizzesQuery } from '../../services/SkillsManagement';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import QuizCard from '../../SystemDesign/QuizCard';

/**
 * SuggestedQuizzesView
 * Horizontal scroll list of suggested quizzes from other postes.
 */
const SuggestedQuizzesView = ({ open = true, posteId, excludeQuizId, onSelect }) => {
  const { data: allQuizzesData, isLoading } = useGetQuizzesQuery(
    { page: 1, limit: 12, isActive: true },
    { skip: !open, refetchOnMountOrArgChange: true }
  );

  const suggestedQuizzes = React.useMemo(() => {
    const arr = allQuizzesData?.data?.quizzes || allQuizzesData?.quizzes || [];
    const list = Array.isArray(arr) ? arr : [];
    const pid = Number(posteId);
    const exq = Number(excludeQuizId);
    return list
      .filter((q) => Number(q?.posteId ?? q?.poste_id) !== pid)
      .filter((q) => Number(q?.quizId ?? q?.id) !== exq)
      .slice(0, 6);
  }, [allQuizzesData, posteId, excludeQuizId]);
  const ref = React.useRef(null);
  const scroll = (dir) => {
    const el = ref.current; if (!el) return;
    const delta = Math.round((el.clientWidth || 600) * 0.2) * (dir === 'left' ? -1 : 1);
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  if (isLoading) {
    return <Typography variant="body2">Loading suggested quizzes...</Typography>;
  }
  if (!Array.isArray(suggestedQuizzes) || suggestedQuizzes.length === 0) {
    return (
      <Box sx={{ width: '100%', maxWidth: 'min(1600px, calc(100vw - 32px))', mx: 'auto' }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Suggested quizzes from other positions</Typography>
        <Typography variant="body2" color="text.secondary">No suggestions available.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 'min(1600px, calc(100vw - 32px))', mx: 'auto' }}>
      {/* <Typography variant="subtitle1" sx={{ mb: 1 }}>Suggested quizzes from other postes</Typography> */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => scroll('left')} sx={{ flex: '0 0 auto' }}>
          <ChevronLeftIcon />
        </IconButton>
        <Box
          ref={ref}
          sx={{
            flex: 1,
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollBehavior: 'smooth',
            pb: 1,
            px: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)',
            maskImage: 'linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)',
          }}
        >
          {suggestedQuizzes.map((q, idx) => {
            const sid = Number(q?.quizId ?? q?.id);
            return (
              <Box key={sid || idx} sx={{ minWidth: 350, maxWidth: 350, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, display: 'flex' }}>
                  <QuizCard quiz={q} index={idx} />
                </Box>
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <Button fullWidth size="small" variant="outlined" onClick={() => onSelect?.(sid)}>
                    Select this quiz
                  </Button>
                </Box>
              </Box>
            );
          })}
        </Box>
        <IconButton size="small" onClick={() => scroll('right')} sx={{ flex: '0 0 auto' }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SuggestedQuizzesView;
